/// <reference types="@types/google.maps" />
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TourRoute } from "@/types/tour";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Map, Crosshair } from "lucide-react";
import { toast } from "sonner";

declare global {
  interface Window {
    google?: typeof google;
  }
}

interface TourMapProps {
  route: TourRoute;
  center: { lat: number; lng: number };
}

export function TourMap({ route, center }: TourMapProps) {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const userCircleRef = useRef<google.maps.Circle | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const [tracking, setTracking] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setError(t("map.key_missing"));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setError(t("map.load_error"));
    document.head.appendChild(script);
  }, []);

  // Initialize / update map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    // Create map if not existing
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center,
        zoom: 14,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a9a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a3e" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1a1a2e" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
          { featureType: "poi", elementType: "geometry", stylers: [{ color: "#1e1e30" }] },
          { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1a2e1a" }] },
        ],
        disableDefaultUI: true,
        zoomControl: true,
      });
    }

    const map = mapInstanceRef.current;

    // Clear old markers & directions
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    directionsRendererRef.current?.setMap(null);
    polylineRef.current?.setMap(null);

    // Add markers
    const bounds = new google.maps.LatLngBounds();
    const path: google.maps.LatLngLiteral[] = [];

    route.schedule.forEach((entry, index) => {
      const position = { lat: entry.bar.lat, lng: entry.bar.lng };
      path.push(position);
      bounds.extend(position);

      const marker = new google.maps.Marker({
        position,
        map,
        label: {
          text: String(index + 1),
          color: "#0f1219",
          fontWeight: "bold",
          fontSize: "14px",
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 16,
          fillColor: "#e89c19",
          fillOpacity: 1,
          strokeColor: "#b47515",
          strokeWeight: 2,
        },
        title: entry.bar.name,
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="color:#111;padding:4px"><strong>${entry.bar.name}</strong><br/>${entry.arrivalTime} - ${entry.departureTime}<br/>⭐ ${entry.bar.rating.toFixed(1)}</div>`,
      });

      marker.addListener("click", () => infoWindow.open(map, marker));
      markersRef.current.push(marker);
    });

    // Draw walking directions or fallback polyline
    if (path.length >= 2) {
      const directionsService = new google.maps.DirectionsService();
      const origin = path[0];
      const destination = path[path.length - 1];
      const waypoints = path.slice(1, -1).map((loc) => ({
        location: loc,
        stopover: true,
      }));

      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: google.maps.TravelMode.WALKING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            const renderer = new google.maps.DirectionsRenderer({
              map,
              directions: result,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#e89c19",
                strokeOpacity: 0.7,
                strokeWeight: 3,
              },
            });
            directionsRendererRef.current = renderer;
          } else {
            // Fallback: straight lines
            polylineRef.current = new google.maps.Polyline({
              path,
              geodesic: true,
              strokeColor: "#e89c19",
              strokeOpacity: 0.7,
              strokeWeight: 3,
              map,
            });
          }
        }
      );
    }

    map.fitBounds(bounds, 60);
  }, [mapLoaded, route, center]);

  // Cleanup geolocation watcher on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      userMarkerRef.current?.setMap(null);
      userCircleRef.current?.setMap(null);
    };
  }, []);

  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    userMarkerRef.current?.setMap(null);
    userCircleRef.current?.setMap(null);
    userMarkerRef.current = null;
    userCircleRef.current = null;
    setTracking(false);
  };

  const startTracking = () => {
    if (!navigator.geolocation || !mapInstanceRef.current) {
      toast.error(t("map.location_error"));
      return;
    }
    const map = mapInstanceRef.current;
    setTracking(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (!userMarkerRef.current) {
          userMarkerRef.current = new google.maps.Marker({
            position: loc,
            map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 3,
            },
            zIndex: 9999,
          });
          userCircleRef.current = new google.maps.Circle({
            map,
            center: loc,
            radius: pos.coords.accuracy || 30,
            fillColor: "#4285F4",
            fillOpacity: 0.15,
            strokeColor: "#4285F4",
            strokeOpacity: 0.4,
            strokeWeight: 1,
          });
        } else {
          userMarkerRef.current.setPosition(loc);
          userCircleRef.current?.setCenter(loc);
          userCircleRef.current?.setRadius(pos.coords.accuracy || 30);
        }
      },
      () => {
        toast.error(t("map.location_error"));
        stopTracking();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  };

  if (error) {
    return (
      <Card className="flex items-center justify-center border-border/50 bg-card p-8 text-center">
        <div>
          <Map className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-border/50 relative">
      <div ref={mapRef} className="h-[400px] w-full bg-muted" />
      {mapLoaded && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={tracking ? stopTracking : startTracking}
          className="absolute top-3 right-3 shadow-lg gap-1.5"
        >
          <Crosshair className={`h-4 w-4 ${tracking ? "text-primary" : ""}`} />
          <span className="hidden sm:inline">
            {tracking ? t("map.hide_location") : t("map.show_location")}
          </span>
        </Button>
      )}
    </Card>
  );
}
